import React, { useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
} from "@material-ui/core";
import { useTranslate } from "react-admin";
import GrowthIndicator from "../common/GrowthIndicator";
import LineGraph from "../common/LineGraph";
import InfoIcon from "@material-ui/icons/Info";
import {
  FuelEconomyHookParams,
  useFuelDrivers,
} from "../hooks/useFuelEconomyHooks";
import "./FuelEconomyReport.css";

type Props = FuelEconomyHookParams;

const formatNumber = (value: number | null | undefined) => {
  if (value == null || Number.isNaN(value)) {
    return "-";
  }
  return value.toFixed(2);
};

export const FuelDriversTableWidget: React.FC<Props> = ({
  fromDate,
  toDate,
  periodType,
}) => {
  const translate = useTranslate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { data, loading: isLoading, error } = useFuelDrivers({
    fromDate,
    toDate,
    periodType,
    skipCount: page * rowsPerPage,
    maxResultCount: rowsPerPage,
  });
  const isError = !!error;
  const rows = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;

  const onPageChange = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);
  const onRowsPerPageChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(e.target.value, 10));
      setPage(0);
    },
    [],
  );

  return (
    <Card className="widget">
      <div className="widget-title">
        <div className="widget-title-icon" style={{ color: "#1976d2" }}>
          <InfoIcon />
        </div>
        <div className="widget-title-label">
          {translate("fuelEconomyReport.driversFuelConsumption", {
            _: "Drivers fuel consumption",
          })}
        </div>
      </div>
      <CardContent style={{ padding: 16 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Typography variant="body2" color="error">
              {translate("common.errorLoadingData", {
                _: "Error loading data",
              })}
            </Typography>
          </Box>
        ) : !rows.length ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Typography variant="body2" color="textSecondary">
              {translate("common.noData", {
                _: "No data",
              })}
            </Typography>
          </Box>
        ) : (
          <TableContainer style={{ overflow: "auto" }}>
            <Table size="small" style={{ minWidth: 1400 }}>
              <TableHead>
                <TableRow>
                  <TableCell>
                    {translate("common.truck", {
                      _: "Truck",
                    })}
                  </TableCell>
                  <TableCell>
                    {translate("common.driver", {
                      _: "Driver",
                    })}
                  </TableCell>
                  <TableCell>
                    {translate("common.average", {
                      _: "Average",
                    })}
                  </TableCell>
                  <TableCell>
                    {translate("common.max", {
                      _: "Max",
                    })}
                  </TableCell>
                  <TableCell>
                    {translate("common.min", {
                      _: "Min",
                    })}
                  </TableCell>
                  <TableCell>
                    {translate("common.speeding", {
                      _: "Speeding",
                    })}
                  </TableCell>
                  <TableCell>
                    {translate("common.accelerate", {
                      _: "Accelerate",
                    })}
                  </TableCell>
                  <TableCell>
                    {translate("common.idle", {
                      _: "Idle",
                    })}
                  </TableCell>
                  <TableCell align="right">
                    {translate("fuelEconomyReport.performance", {
                      _: "Performance",
                    })}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow
                    key={`${row.vehicleName}-${row.driverFullName}-${index}`}
                  >
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: "0.875rem" }}
                      >
                        #{row.vehicleName ?? "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {row.driverFullName ?? "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {row.fuelConsumptionAverage != null
                          ? `${formatNumber(
                              row.fuelConsumptionAverage,
                            )} ${translate("common.mpg", {
                              _: "mpg",
                            })}`
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {row.maxFuelConsumption != null
                          ? `${formatNumber(
                              row.maxFuelConsumption,
                            )} ${translate("common.mpg", {
                              _: "mpg",
                            })}`
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {row.minFuelConsumption != null
                          ? `${formatNumber(
                              row.minFuelConsumption,
                            )} ${translate("common.mpg", {
                              _: "mpg",
                            })}`
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {row.speedingFuelConsumption != null
                          ? `${formatNumber(
                              row.speedingFuelConsumption,
                            )} ${translate("common.mpg", {
                              _: "mpg",
                            })}`
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {row.accelerationFuelConsumption != null
                          ? `${formatNumber(
                              row.accelerationFuelConsumption,
                            )} ${translate("common.mpg", {
                              _: "mpg",
                            })}`
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {row.idleFuelConsumption != null
                          ? `${formatNumber(
                              row.idleFuelConsumption,
                            )} ${translate("common.gal", {
                              _: "gal",
                            })}`
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-end"
                      >
                        {row.eventScale && (
                          <LineGraph
                            data={row.eventScale}
                            width={115}
                            height={24}
                            isGrowing={row.isGrowing ?? false}
                            isReverse={false}
                          />
                        )}
                        {row.growth !== undefined && row.growth !== null && (
                          <GrowthIndicator
                            growth={row.growth}
                            isGrowing={row.isGrowing ?? false}
                            isReverse={false}
                          />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={onPageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={onRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage={translate("common.rowsPerPage", {
                _: "Rows per page",
              })}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} ${translate("common.of", { _: "of" })} ${
                  count !== -1 ? count : `more than ${to}`
                }`
              }
            />
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default FuelDriversTableWidget;
